import React from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import { isEmpty, isNil } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { useClaim } from '~/components/ClaimContainer';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import Text from '~/components/core/TextComponents/Text';
import DocumentFieldsSection from '~/components/Documents/UploadDocumentsCard/DocumentFieldsSection/DocumentFieldsSection';
import { documentFieldsValidation, fileNameToDocumentName } from '~/components/Documents/UploadDocumentsCard/utils';
import { useCms } from '~/components/hooks/useCms';
import { serverDateTimeToLocalDate } from '~/DateTimeUtils';
import type { AttachRequest, AttachResponse } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import type { FileRow } from './DocumentRequestsSection/DocumentRequestFilesTable';
import { TEXTS } from './texts';
import { documentRequestAttachUrl } from './useDocumentRequests';

const VALIDATION_SCHEMA = Yup.object().shape(documentFieldsValidation({ isClaimIdRequired: false }));

interface AttachDocumentRequestFileDialogProps {
  file: FileRow;
  onClose: () => void;
  onAttached: () => Promise<void>;
}

const AttachDocumentRequestFileDialog: React.FC<AttachDocumentRequestFileDialogProps> = ({
  file,
  onClose,
  onAttached,
}) => {
  const { claim } = useClaim({ claimRequired: true });
  const { user } = useCms();
  const ownExposureId = claim.exposures?.find((exposure) => exposure?.handling_adjuster_id === user.id)?.id;
  // Both dates start as the day the claimant submitted the files, as the email attach starts from the day it came in.
  const submitDate = isNil(file.submitted_at) ? undefined : serverDateTimeToLocalDate(file.submitted_at);

  const initialValues = {
    type: '',
    document_name: fileNameToDocumentName(file.name),
    document_date: submitDate,
    document_date_received: submitDate,
    exposure_ids: isNil(ownExposureId) ? [] : [ownExposureId],
    summary: '',
  };

  const handleSubmit = async (values: typeof initialValues) => {
    const payload: AttachRequest = {
      selections: [
        {
          uploaded_file_id: file.uploaded_file_id,
          document_type: values.type,
          // A blank name is left to the server, which names the document after the file.
          document_name: isEmpty(values.document_name) ? null : values.document_name,
          summary: values.summary,
          exposure_ids: values.exposure_ids,
          document_date: values.document_date,
          // A cleared picker holds '', which the route would refuse; null leaves it to the submit date.
          document_date_received: isEmpty(values.document_date_received) ? null : values.document_date_received,
        },
      ],
    };
    try {
      await axios.post<AttachResponse>(
        documentRequestAttachUrl(claim.organization_id, claim.id, file.document_request_id),
        payload
      );
    } catch (error) {
      await reportAxiosError(error);
      // A transient failure keeps the dialog and its values so the adjuster can retry.
      if (!axios.isAxiosError(error) || isNil(error.response) || error.response.status >= 500) {
        return;
      }
    }
    // On success the list loses the Attach action and the claim gains the document; a 4xx means the row
    // moved (409) or is gone (404). Either way, refresh.
    await onAttached();
    onClose();
  };

  return (
    <Formik initialValues={initialValues} validationSchema={VALIDATION_SCHEMA} onSubmit={handleSubmit}>
      {({ isSubmitting, handleSubmit: submit }) => (
        <CardDialog
          isDialog
          title={TEXTS.ATTACH_DIALOG_TITLE}
          maxWidth="xs"
          onClose={onClose}
          preventClose={isSubmitting}
          footerActions={
            <DialogFooterActions
              primaryLabel={TEXTS.ATTACH_SUBMIT}
              onClickPrimary={() => submit()}
              onClickSecondary={onClose}
              disabled={isSubmitting}
            />
          }
        >
          <Text variant={Text.VARIANTS.SM} className="mb-16">
            {TEXTS.ATTACH_DIALOG_FILE(file.name)}
          </Text>
          <DocumentFieldsSection claim={claim} disabled={isSubmitting} />
        </CardDialog>
      )}
    </Formik>
  );
};

export default AttachDocumentRequestFileDialog;
